import React, { useState, useRef } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
  Upload,
  Link as LinkIcon,
  X,
  Image as ImageIcon,
  Loader2
} from 'lucide-react';

const ImageUploader = ({ 
  value, 
  onChange, 
  label = "Image",
  placeholder = "Enter image URL or upload a file"
}) => {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState(value ? 'url' : 'upload'); // 'upload' or 'url'
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = reader.result.split(',')[1];
          const response = await axios.post(`${API}/upload/image`, {
            data: base64,
            filename: file.name
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          onChange(response.data.url);
          toast.success('Image uploaded successfully');
        } catch (error) {
          console.error('Upload failed:', error);
          toast.error('Failed to upload image');
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error reading file:', error);
      toast.error('Failed to read file');
      setUploading(false);
    }
  };

  const handleUrlChange = (e) => {
    onChange(e.target.value);
  };

  const handleClear = () => {
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => setMode('upload')}
            className={`px-3 py-1 text-xs rounded-md transition-colors ${
              mode === 'upload' 
                ? 'bg-white text-plp-blue shadow-sm' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Upload className="w-3 h-3 inline mr-1" />
            Upload
          </button>
          <button
            type="button"
            onClick={() => setMode('url')}
            className={`px-3 py-1 text-xs rounded-md transition-colors ${
              mode === 'url' 
                ? 'bg-white text-plp-blue shadow-sm' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <LinkIcon className="w-3 h-3 inline mr-1" />
            URL
          </button>
        </div>
      </div>

      {mode === 'upload' ? (
        <div className="space-y-2">
          <div 
            className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
              uploading ? 'border-plp-blue bg-plp-blue/5' : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              id="image-upload"
            />
            <label 
              htmlFor="image-upload" 
              className="cursor-pointer block"
            >
              {uploading ? (
                <div className="py-4">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-plp-blue mb-2" />
                  <p className="text-sm text-slate-500">Uploading...</p>
                </div>
              ) : (
                <div className="py-4">
                  <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-600">Click to upload image</p>
                  <p className="text-xs text-slate-400 mt-1">PNG, JPG, GIF up to 5MB</p>
                </div>
              )}
            </label>
          </div>
        </div>
      ) : (
        <Input
          value={value || ''}
          onChange={handleUrlChange}
          placeholder={placeholder}
          className="w-full"
        />
      )}

      {/* Preview */}
      {value && (
        <div className="relative">
          <div className="rounded-lg overflow-hidden border bg-slate-50">
            <img 
              src={value.startsWith('/api') ? `${API.replace('/api', '')}${value}` : value}
              alt="Preview" 
              className="w-full h-32 object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden items-center justify-center h-32 text-slate-400">
              <ImageIcon className="w-8 h-8" />
            </div>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
